import React from "react"
import { snakeCase } from "lodash"
import { MenuItemCustomizationList } from "../MenuItemCustomizationList"
import { CustomizeButton } from "../CustomizeButton"
import { Tag } from "../Tag"
import { SIGNUP_URL } from "../../constants/urls"

interface Variant {
  name: string,
  visible?: boolean
}

interface ITag {
  name: string,
  icon?: string
}

interface Category {
  id: Number,
  name: string,
  sortOrder: Number
}

export interface MenuItemProps {
  showAnchor?: boolean,
  customizable?: boolean,
  title?: string,
  image?: string,
  allergens?: string[],
  classifications?: ITag[],
  defaultVariant?: Variant,
  variantOptions?: Variant[],
  category?: Category,
  designVariation?: boolean
}

interface MenuItemState {
  variantsOpen: boolean
}

class MenuItemInner extends React.Component<MenuItemProps, MenuItemState> {
  static defaultProps: MenuItemProps = {
    showAnchor: false,
    variantOptions: [],
    allergens: [],
    classifications: [],
    designVariation: false,
  }

  constructor(props = {}) {
    super(props)

    this.state = {
      variantsOpen: false,
    }

    this.toggleVariants = this.toggleVariants.bind(this)
    this.renderAnchor = this.renderAnchor.bind(this)
  }

  toggleVariants() {
    const { variantsOpen } = this.state
    this.setState({ variantsOpen: !variantsOpen })
  }

  determineLinkingWord() {
    const { title } = this.props
    let word = "with"

    if (title.includes(word)) {
      word = "and"
    }

    return word
  }

  renderClassification(classification) {
    return (
      <Tag {...classification} />
    )
  }

  renderAllergens() {
    const { allergens } = this.props

    if (allergens.length > 1) {
      const formatted = allergens.join(", ")

      return (
        <span className="block mt-3 text-xs text-gray-700 italic">
          Contains:
          {" "}
          {formatted}
        </span>
      )
    }

    return (<></>)
  }

  renderCustomizeButton() {
    const { variantOptions, customizable, designVariation } = this.props

    if (customizable && !designVariation) {
      return (
        <CustomizeButton
          onClick={() => this.toggleVariants()}
          count={variantOptions.length}
        />
      )
    }
  }

  renderDefaultVariant() {
    const { defaultVariant, customizable, designVariation } = this.props
    const variantVisible = defaultVariant && defaultVariant.visible

    if (variantVisible && customizable && !designVariation) {
      return (
        <div className="font-600 text-base">
          {this.determineLinkingWord()}
          {" "}
          {defaultVariant.name}
        </div>
      )
    }
  }

  renderProtein() {
    const { variantOptions, customizable, designVariation } = this.props

    if (customizable && designVariation) {
      const onClick = () => this.toggleVariants()
      const count = variantOptions.length
      return (
        <div className="font-600 text-base">
          and your&nbsp;
          <a className="text-black-secondary font-bold cursor-pointer underline" onClick={() => onClick()}>
            choice of protein (
            {count}
            )
          </a>
          .
        </div>
      )
    }
  }

  renderDetails() {
    const { title, image, classifications } = this.props
    const { variantsOpen } = this.state

    if (!variantsOpen) {
      return (
        <>
          <div className="relative">
            <a href={SIGNUP_URL} className="flex-grow-0">
              <img alt={title} src={image} className="rounded-t-lg" />
            </a>
            {this.renderCustomizeButton()}
          </div>
          <div className="p-4 flex flex flex-col content-between h-full flex-grow">
            <div className="flex-grow mb-2">
              <a href={SIGNUP_URL} className="text-xl font-semibold leading-8">
                {title}
              </a>
              {this.renderDefaultVariant()}
              {this.renderProtein()}
            </div>
            <aside>
              {this.renderAllergens()}
              <div className="flex">
                {classifications.map(this.renderClassification)}
              </div>
            </aside>
          </div>
        </>
      )
    }
  }

  renderVariantOptions() {
    const { variantOptions } = this.props
    const { variantsOpen } = this.state

    const props = {
      variantOptions,
      onClose: this.toggleVariants,
    }

    if (variantsOpen) {
      return (
        <MenuItemCustomizationList {...props} />
      )
    }
  }

  renderAnchor() {
    const { category, showAnchor } = this.props

    if (showAnchor) {
      return snakeCase(category.name)
    }
  }

  render() {
    return (
      <article
        className="flex flex-col rounded-lg shadow-[0_4px_8px_0px_rgba(0,0,0,0.2)] bg-white text-black-secondary"
        id={this.renderAnchor()}
      >
        {this.renderDetails()}
        {this.renderVariantOptions()}
      </article>
    )
  }
}

export function MenuItem(props: MenuItemProps) {
  return <MenuItemInner {...props} />
}
